/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Recap
 * OpenAPI spec version: 1
 */
import * as zod from 'zod';

import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useApiFetch } from '../client.ts';
import type { ErrorType } from '../client.ts';
import { useMutationBehavior } from '../mutations.ts';
import { useQueryBehavior } from '../queries.ts';
import {
    EmployeeRecap,
    EmployeeRecapVersionListItem,
    ExternalLink,
    PagedEmployeeRecapListItem,
    PagedPublicRecapListItem,
    PagedRecapListEntry,
    PublicRecap,
    Recap,
    RecapChanges,
    RecapVersion,
    RecapVersionDetail,
    RecapVersionListItem,
    SharedRecap,
    SupersedableItem,
} from './schemas.ts';
import type {
    DecisionInput,
    ErrorResponse,
    EventInput,
    ItemMove,
    ListEmployeeRecapsParams,
    ListPublicRecapsParams,
    ListRecapsParams,
    Move,
    PersonInput,
    RecapAcknowledgersUpdate,
    RecapCreate,
    RecapProjectUpdate,
    RecapPublish,
    RecapSourceFilesUpdate,
    RecapUpdate,
    TodoDone,
    TodoExternalLinkInput,
    TodoInput,
    TopicInput,
} from './schemas.ts';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

export const getListPublicRecapsUrl = (token: string | null, params?: ListPublicRecapsParams) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(key, value === null ? 'null' : String(value));
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0
        ? `/public/projects/${token}/recaps?${stringifiedParams}`
        : `/public/projects/${token}/recaps`;
};

/**
 * @summary List Public Recaps
 */
export const useListPublicRecapsHook = (): ((
    token: string | null,
    params?: ListPublicRecapsParams,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<PagedPublicRecapListItem>) => {
    const customFetcher = useApiFetch();
    return (
        token: string | null,
        params?: ListPublicRecapsParams,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getListPublicRecapsUrl(token, params), {
            ...options,
            method: 'GET',

            schema: PagedPublicRecapListItem,
        });
    };
};

export const getListPublicRecapsQueryKey = (token: string | null, params?: ListPublicRecapsParams) => {
    return ['public', 'projects', token, 'recaps', ...(params ? [params] : [])] as const;
};

export const useListPublicRecapsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    params?: ListPublicRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListPublicRecapsQueryKey(token, params);

    const listPublicRecaps = useListPublicRecapsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>> = ({ signal }) =>
        listPublicRecaps(token, params, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token, params },
        {
            url: `/public/projects/${token}/recaps`,
            operationId: 'list_public_recaps',
            operationName: 'listPublicRecaps',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListPublicRecapsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>>;
export type ListPublicRecapsQueryError = ErrorType<ErrorResponse>;

export function useListPublicRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    params: undefined | ListPublicRecapsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPublicRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    params?: ListPublicRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPublicRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    params?: ListPublicRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Public Recaps
 */

export function useListPublicRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    params?: ListPublicRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListPublicRecapsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListPublicRecapsQueryOptions(token, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetPublicRecapUrl = (token: string | null, publicId: string | null) => {
    return `/public/projects/${token}/recaps/${publicId}`;
};

/**
 * @summary Get Public Recap
 */
export const useGetPublicRecapHook = (): ((
    token: string | null,
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<PublicRecap>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetPublicRecapUrl(token, publicId), {
            ...options,
            method: 'GET',

            schema: PublicRecap,
        });
    };
};

export const getGetPublicRecapQueryKey = (token: string | null, publicId: string | null) => {
    return ['public', 'projects', token, 'recaps', publicId] as const;
};

export const useGetPublicRecapQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPublicRecapQueryKey(token, publicId);

    const getPublicRecap = useGetPublicRecapHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>> = ({ signal }) =>
        getPublicRecap(token, publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        {
            queryKey,
            queryFn,
            enabled: token !== null && token !== undefined && publicId !== null && publicId !== undefined,
            ...queryOptions,
        },
        { token, publicId },
        {
            url: `/public/projects/${token}/recaps/${publicId}`,
            operationId: 'get_public_recap',
            operationName: 'getPublicRecap',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPublicRecapQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>>;
export type GetPublicRecapQueryError = ErrorType<ErrorResponse>;

export function useGetPublicRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    publicId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Public Recap
 */

export function useGetPublicRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPublicRecapQueryOptions(token, publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetSharedRecapUrl = (token: string | null) => {
    return `/public/recaps/${token}`;
};

/**
 * @summary Get Shared Recap
 */
export const useGetSharedRecapHook = (): ((
    token: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SharedRecap>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetSharedRecapUrl(token), {
            ...options,
            method: 'GET',

            schema: SharedRecap,
        });
    };
};

export const getGetSharedRecapQueryKey = (token: string | null) => {
    return ['public', 'recaps', token] as const;
};

export const useGetSharedRecapQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetSharedRecapQueryKey(token);

    const getSharedRecap = useGetSharedRecapHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>> = ({ signal }) =>
        getSharedRecap(token, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token },
        { url: `/public/recaps/${token}`, operationId: 'get_shared_recap', operationName: 'getSharedRecap' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSharedRecapQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>>;
export type GetSharedRecapQueryError = ErrorType<ErrorResponse>;

export function useGetSharedRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Shared Recap
 */

export function useGetSharedRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetSharedRecapQueryOptions(token, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetSharedRecapClientLogoUrl = (token: string | null) => {
    return `/public/recaps/${token}/client/logo`;
};

/**
 * @summary Get Shared Recap Client Logo
 */
export const useGetSharedRecapClientLogoHook = (): ((
    token: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetSharedRecapClientLogoUrl(token), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetSharedRecapClientLogoQueryKey = (token: string | null) => {
    return ['public', 'recaps', token, 'client', 'logo'] as const;
};

export const useGetSharedRecapClientLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetSharedRecapClientLogoQueryKey(token);

    const getSharedRecapClientLogo = useGetSharedRecapClientLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>> = ({
        signal,
    }) => getSharedRecapClientLogo(token, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token },
        {
            url: `/public/recaps/${token}/client/logo`,
            operationId: 'get_shared_recap_client_logo',
            operationName: 'getSharedRecapClientLogo',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSharedRecapClientLogoQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>
>;
export type GetSharedRecapClientLogoQueryError = ErrorType<ErrorResponse>;

export function useGetSharedRecapClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedRecapClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedRecapClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Shared Recap Client Logo
 */

export function useGetSharedRecapClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetSharedRecapClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetSharedRecapClientLogoQueryOptions(token, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getListRecapsUrl = (params?: ListRecapsParams) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(key, value === null ? 'null' : String(value));
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0 ? `/workspace/recaps?${stringifiedParams}` : `/workspace/recaps`;
};

/**
 * @summary List Recaps
 */
export const useListRecapsHook = (): ((
    params?: ListRecapsParams,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<PagedRecapListEntry>) => {
    const customFetcher = useApiFetch();
    return (params?: ListRecapsParams, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListRecapsUrl(params), {
            ...options,
            method: 'GET',

            schema: PagedRecapListEntry,
        });
    };
};

export const getListRecapsQueryKey = (params?: ListRecapsParams) => {
    return ['workspace', 'recaps', ...(params ? [params] : [])] as const;
};

export const useListRecapsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListRecapsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListRecapsQueryKey(params);

    const listRecaps = useListRecapsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>> = ({ signal }) =>
        listRecaps(params, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        { params },
        { url: `/workspace/recaps`, operationId: 'list_recaps', operationName: 'listRecaps' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListRecapsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>>;
export type ListRecapsQueryError = ErrorType<ErrorResponse>;

export function useListRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params: undefined | ListRecapsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListRecapsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListRecapsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Recaps
 */

export function useListRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListRecapsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListRecapsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListRecapsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateRecapFromSourcesUrl = () => {
    return `/workspace/recaps`;
};

/**
 * @summary Create Recap From Sources
 */
export const useCreateRecapFromSourcesHook = (): ((
    recapCreate: RecapCreate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (recapCreate: RecapCreate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateRecapFromSourcesUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(recapCreate),
            schema: Recap,
        });
    };
};

export const getCreateRecapFromSourcesMutationKey = () => ['createRecapFromSources'] as const;

export const useCreateRecapFromSourcesMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateRecapFromSourcesHook>>>,
        TError,
        CreateRecapFromSourcesMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateRecapFromSourcesHook>>>,
    TError,
    CreateRecapFromSourcesMutationVariables,
    TContext
> => {
    const mutationKey = getCreateRecapFromSourcesMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createRecapFromSources = useCreateRecapFromSourcesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateRecapFromSourcesHook>>>,
        CreateRecapFromSourcesMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return createRecapFromSources(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps` },
        { operationId: 'create_recap_from_sources', operationName: 'createRecapFromSources' }
    );

    return customOptions;
};

export type CreateRecapFromSourcesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateRecapFromSourcesHook>>>
>;
export type CreateRecapFromSourcesMutationBody = RecapCreate;
export type CreateRecapFromSourcesMutationError = ErrorType<ErrorResponse>;
export type CreateRecapFromSourcesMutationVariables = { data: RecapCreate };

/**
 * @summary Create Recap From Sources
 */
export const useCreateRecapFromSources = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateRecapFromSourcesHook>>>,
            TError,
            CreateRecapFromSourcesMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateRecapFromSourcesHook>>>,
    TError,
    CreateRecapFromSourcesMutationVariables,
    TContext
> => {
    return useMutation(useCreateRecapFromSourcesMutationOptions(options), queryClient);
};
export const getGetRecapUrl = (publicId: string | null) => {
    return `/workspace/recaps/${publicId}`;
};

/**
 * @summary Get Recap
 */
export const useGetRecapHook = (): ((
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetRecapUrl(publicId), {
            ...options,
            method: 'GET',

            schema: Recap,
        });
    };
};

export const getGetRecapQueryKey = (publicId: string | null) => {
    return ['workspace', 'recaps', publicId] as const;
};

export const useGetRecapQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetRecapQueryKey(publicId);

    const getRecap = useGetRecapHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>> = ({ signal }) =>
        getRecap(publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions },
        { publicId },
        { url: `/workspace/recaps/${publicId}`, operationId: 'get_recap', operationName: 'getRecap' }
    );

    return customOptions as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type GetRecapQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>>;
export type GetRecapQueryError = ErrorType<ErrorResponse>;

export function useGetRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Recap
 */

export function useGetRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRecapHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetRecapQueryOptions(publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateRecapUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}`;
};

/**
 * @summary Update Recap
 */
export const useUpdateRecapHook = (): ((
    publicId: string,
    recapUpdate: RecapUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, recapUpdate: RecapUpdate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getUpdateRecapUrl(publicId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(recapUpdate),
            schema: Recap,
        });
    };
};

export const getUpdateRecapMutationKey = () => ['updateRecap'] as const;

export const useUpdateRecapMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateRecapHook>>>,
        TError,
        UpdateRecapMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateRecapHook>>>,
    TError,
    UpdateRecapMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateRecapMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateRecap = useUpdateRecapHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateRecapHook>>>,
        UpdateRecapMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return updateRecap(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}` },
        { operationId: 'update_recap', operationName: 'updateRecap' }
    );

    return customOptions;
};

export type UpdateRecapMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateRecapHook>>>>;
export type UpdateRecapMutationBody = RecapUpdate;
export type UpdateRecapMutationError = ErrorType<ErrorResponse>;
export type UpdateRecapMutationVariables = { publicId: string; data: RecapUpdate };

/**
 * @summary Update Recap
 */
export const useUpdateRecap = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateRecapHook>>>,
            TError,
            UpdateRecapMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateRecapHook>>>,
    TError,
    UpdateRecapMutationVariables,
    TContext
> => {
    return useMutation(useUpdateRecapMutationOptions(options), queryClient);
};
export const getDeleteRecapUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}`;
};

/**
 * @summary Delete Recap
 */
export const useDeleteRecapHook = (): ((
    publicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteRecapUrl(publicId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getDeleteRecapMutationKey = () => ['deleteRecap'] as const;

export const useDeleteRecapMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteRecapHook>>>,
        TError,
        DeleteRecapMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteRecapHook>>>,
    TError,
    DeleteRecapMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteRecapMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteRecap = useDeleteRecapHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteRecapHook>>>,
        DeleteRecapMutationVariables
    > = (props) => {
        const { publicId } = props ?? {};

        return deleteRecap(publicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}` },
        { operationId: 'delete_recap', operationName: 'deleteRecap' }
    );

    return customOptions;
};

export type DeleteRecapMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteRecapHook>>>>;

export type DeleteRecapMutationError = ErrorType<ErrorResponse>;
export type DeleteRecapMutationVariables = { publicId: string };

/**
 * @summary Delete Recap
 */
export const useDeleteRecap = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteRecapHook>>>,
            TError,
            DeleteRecapMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteRecapHook>>>,
    TError,
    DeleteRecapMutationVariables,
    TContext
> => {
    return useMutation(useDeleteRecapMutationOptions(options), queryClient);
};
export const getUpdateRecapProjectUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/project`;
};

/**
 * @summary Update Recap Project
 */
export const useUpdateRecapProjectHook = (): ((
    publicId: string,
    recapProjectUpdate: RecapProjectUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        recapProjectUpdate: RecapProjectUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateRecapProjectUrl(publicId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(recapProjectUpdate),
            schema: Recap,
        });
    };
};

export const getUpdateRecapProjectMutationKey = () => ['updateRecapProject'] as const;

export const useUpdateRecapProjectMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateRecapProjectHook>>>,
        TError,
        UpdateRecapProjectMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateRecapProjectHook>>>,
    TError,
    UpdateRecapProjectMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateRecapProjectMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateRecapProject = useUpdateRecapProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateRecapProjectHook>>>,
        UpdateRecapProjectMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return updateRecapProject(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/project` },
        { operationId: 'update_recap_project', operationName: 'updateRecapProject' }
    );

    return customOptions;
};

export type UpdateRecapProjectMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateRecapProjectHook>>>
>;
export type UpdateRecapProjectMutationBody = RecapProjectUpdate;
export type UpdateRecapProjectMutationError = ErrorType<ErrorResponse>;
export type UpdateRecapProjectMutationVariables = { publicId: string; data: RecapProjectUpdate };

/**
 * @summary Update Recap Project
 */
export const useUpdateRecapProject = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateRecapProjectHook>>>,
            TError,
            UpdateRecapProjectMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateRecapProjectHook>>>,
    TError,
    UpdateRecapProjectMutationVariables,
    TContext
> => {
    return useMutation(useUpdateRecapProjectMutationOptions(options), queryClient);
};
export const getProcessRecapUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/process`;
};

/**
 * @summary Process Recap
 */
export const useProcessRecapHook = (): ((
    publicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getProcessRecapUrl(publicId), {
            ...options,
            method: 'POST',

            schema: Recap,
        });
    };
};

export const getProcessRecapMutationKey = () => ['processRecap'] as const;

export const useProcessRecapMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useProcessRecapHook>>>,
        TError,
        ProcessRecapMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useProcessRecapHook>>>,
    TError,
    ProcessRecapMutationVariables,
    TContext
> => {
    const mutationKey = getProcessRecapMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const processRecap = useProcessRecapHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useProcessRecapHook>>>,
        ProcessRecapMutationVariables
    > = (props) => {
        const { publicId } = props ?? {};

        return processRecap(publicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/process` },
        { operationId: 'process_recap', operationName: 'processRecap' }
    );

    return customOptions;
};

export type ProcessRecapMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useProcessRecapHook>>>>;

export type ProcessRecapMutationError = ErrorType<ErrorResponse>;
export type ProcessRecapMutationVariables = { publicId: string };

/**
 * @summary Process Recap
 */
export const useProcessRecap = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useProcessRecapHook>>>,
            TError,
            ProcessRecapMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useProcessRecapHook>>>,
    TError,
    ProcessRecapMutationVariables,
    TContext
> => {
    return useMutation(useProcessRecapMutationOptions(options), queryClient);
};
export const getUpdateSourceFilesUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/source-files`;
};

/**
 * @summary Update Source Files
 */
export const useUpdateSourceFilesHook = (): ((
    publicId: string,
    recapSourceFilesUpdate: RecapSourceFilesUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        recapSourceFilesUpdate: RecapSourceFilesUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateSourceFilesUrl(publicId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(recapSourceFilesUpdate),
            schema: Recap,
        });
    };
};

export const getUpdateSourceFilesMutationKey = () => ['updateSourceFiles'] as const;

export const useUpdateSourceFilesMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateSourceFilesHook>>>,
        TError,
        UpdateSourceFilesMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourceFilesHook>>>,
    TError,
    UpdateSourceFilesMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateSourceFilesMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateSourceFiles = useUpdateSourceFilesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateSourceFilesHook>>>,
        UpdateSourceFilesMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return updateSourceFiles(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/source-files` },
        { operationId: 'update_source_files', operationName: 'updateSourceFiles' }
    );

    return customOptions;
};

export type UpdateSourceFilesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourceFilesHook>>>
>;
export type UpdateSourceFilesMutationBody = RecapSourceFilesUpdate;
export type UpdateSourceFilesMutationError = ErrorType<ErrorResponse>;
export type UpdateSourceFilesMutationVariables = { publicId: string; data: RecapSourceFilesUpdate };

/**
 * @summary Update Source Files
 */
export const useUpdateSourceFiles = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateSourceFilesHook>>>,
            TError,
            UpdateSourceFilesMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateSourceFilesHook>>>,
    TError,
    UpdateSourceFilesMutationVariables,
    TContext
> => {
    return useMutation(useUpdateSourceFilesMutationOptions(options), queryClient);
};
export const getCreateRecapPublicLinkUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/public-link`;
};

/**
 * @summary Create Recap Public Link
 */
export const useCreateRecapPublicLinkHook = (): ((
    publicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateRecapPublicLinkUrl(publicId), {
            ...options,
            method: 'POST',

            schema: Recap,
        });
    };
};

export const getCreateRecapPublicLinkMutationKey = () => ['createRecapPublicLink'] as const;

export const useCreateRecapPublicLinkMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateRecapPublicLinkHook>>>,
        TError,
        CreateRecapPublicLinkMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateRecapPublicLinkHook>>>,
    TError,
    CreateRecapPublicLinkMutationVariables,
    TContext
> => {
    const mutationKey = getCreateRecapPublicLinkMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createRecapPublicLink = useCreateRecapPublicLinkHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateRecapPublicLinkHook>>>,
        CreateRecapPublicLinkMutationVariables
    > = (props) => {
        const { publicId } = props ?? {};

        return createRecapPublicLink(publicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/public-link` },
        { operationId: 'create_recap_public_link', operationName: 'createRecapPublicLink' }
    );

    return customOptions;
};

export type CreateRecapPublicLinkMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateRecapPublicLinkHook>>>
>;

export type CreateRecapPublicLinkMutationError = ErrorType<ErrorResponse>;
export type CreateRecapPublicLinkMutationVariables = { publicId: string };

/**
 * @summary Create Recap Public Link
 */
export const useCreateRecapPublicLink = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateRecapPublicLinkHook>>>,
            TError,
            CreateRecapPublicLinkMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateRecapPublicLinkHook>>>,
    TError,
    CreateRecapPublicLinkMutationVariables,
    TContext
> => {
    return useMutation(useCreateRecapPublicLinkMutationOptions(options), queryClient);
};
export const getDeleteRecapPublicLinkUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/public-link`;
};

/**
 * @summary Delete Recap Public Link
 */
export const useDeleteRecapPublicLinkHook = (): ((
    publicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteRecapPublicLinkUrl(publicId), {
            ...options,
            method: 'DELETE',

            schema: Recap,
        });
    };
};

export const getDeleteRecapPublicLinkMutationKey = () => ['deleteRecapPublicLink'] as const;

export const useDeleteRecapPublicLinkMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteRecapPublicLinkHook>>>,
        TError,
        DeleteRecapPublicLinkMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteRecapPublicLinkHook>>>,
    TError,
    DeleteRecapPublicLinkMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteRecapPublicLinkMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteRecapPublicLink = useDeleteRecapPublicLinkHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteRecapPublicLinkHook>>>,
        DeleteRecapPublicLinkMutationVariables
    > = (props) => {
        const { publicId } = props ?? {};

        return deleteRecapPublicLink(publicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/public-link` },
        { operationId: 'delete_recap_public_link', operationName: 'deleteRecapPublicLink' }
    );

    return customOptions;
};

export type DeleteRecapPublicLinkMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteRecapPublicLinkHook>>>
>;

export type DeleteRecapPublicLinkMutationError = ErrorType<ErrorResponse>;
export type DeleteRecapPublicLinkMutationVariables = { publicId: string };

/**
 * @summary Delete Recap Public Link
 */
export const useDeleteRecapPublicLink = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteRecapPublicLinkHook>>>,
            TError,
            DeleteRecapPublicLinkMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteRecapPublicLinkHook>>>,
    TError,
    DeleteRecapPublicLinkMutationVariables,
    TContext
> => {
    return useMutation(useDeleteRecapPublicLinkMutationOptions(options), queryClient);
};
export const getCreatePersonUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/persons`;
};

/**
 * @summary Create Person
 */
export const useCreatePersonHook = (): ((
    publicId: string,
    personInput: PersonInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, personInput: PersonInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreatePersonUrl(publicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(personInput),
            schema: Recap,
        });
    };
};

export const getCreatePersonMutationKey = () => ['createPerson'] as const;

export const useCreatePersonMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreatePersonHook>>>,
        TError,
        CreatePersonMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreatePersonHook>>>,
    TError,
    CreatePersonMutationVariables,
    TContext
> => {
    const mutationKey = getCreatePersonMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createPerson = useCreatePersonHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreatePersonHook>>>,
        CreatePersonMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return createPerson(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/persons` },
        { operationId: 'create_person', operationName: 'createPerson' }
    );

    return customOptions;
};

export type CreatePersonMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreatePersonHook>>>>;
export type CreatePersonMutationBody = PersonInput;
export type CreatePersonMutationError = ErrorType<ErrorResponse>;
export type CreatePersonMutationVariables = { publicId: string; data: PersonInput };

/**
 * @summary Create Person
 */
export const useCreatePerson = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreatePersonHook>>>,
            TError,
            CreatePersonMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreatePersonHook>>>,
    TError,
    CreatePersonMutationVariables,
    TContext
> => {
    return useMutation(useCreatePersonMutationOptions(options), queryClient);
};
export const getUpdatePersonUrl = (publicId: string, personId: string) => {
    return `/workspace/recaps/${publicId}/persons/${personId}`;
};

/**
 * @summary Update Person
 */
export const useUpdatePersonHook = (): ((
    publicId: string,
    personId: string,
    personInput: PersonInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        personId: string,
        personInput: PersonInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdatePersonUrl(publicId, personId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(personInput),
            schema: Recap,
        });
    };
};

export const getUpdatePersonMutationKey = () => ['updatePerson'] as const;

export const useUpdatePersonMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdatePersonHook>>>,
        TError,
        UpdatePersonMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdatePersonHook>>>,
    TError,
    UpdatePersonMutationVariables,
    TContext
> => {
    const mutationKey = getUpdatePersonMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updatePerson = useUpdatePersonHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdatePersonHook>>>,
        UpdatePersonMutationVariables
    > = (props) => {
        const { publicId, personId, data } = props ?? {};

        return updatePerson(publicId, personId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/persons/{personId}` },
        { operationId: 'update_person', operationName: 'updatePerson' }
    );

    return customOptions;
};

export type UpdatePersonMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdatePersonHook>>>>;
export type UpdatePersonMutationBody = PersonInput;
export type UpdatePersonMutationError = ErrorType<ErrorResponse>;
export type UpdatePersonMutationVariables = { publicId: string; personId: string; data: PersonInput };

/**
 * @summary Update Person
 */
export const useUpdatePerson = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdatePersonHook>>>,
            TError,
            UpdatePersonMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdatePersonHook>>>,
    TError,
    UpdatePersonMutationVariables,
    TContext
> => {
    return useMutation(useUpdatePersonMutationOptions(options), queryClient);
};
export const getDeletePersonUrl = (publicId: string, personId: string) => {
    return `/workspace/recaps/${publicId}/persons/${personId}`;
};

/**
 * @summary Delete Person
 */
export const useDeletePersonHook = (): ((
    publicId: string,
    personId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, personId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeletePersonUrl(publicId, personId), {
            ...options,
            method: 'DELETE',

            schema: Recap,
        });
    };
};

export const getDeletePersonMutationKey = () => ['deletePerson'] as const;

export const useDeletePersonMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeletePersonHook>>>,
        TError,
        DeletePersonMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeletePersonHook>>>,
    TError,
    DeletePersonMutationVariables,
    TContext
> => {
    const mutationKey = getDeletePersonMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deletePerson = useDeletePersonHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeletePersonHook>>>,
        DeletePersonMutationVariables
    > = (props) => {
        const { publicId, personId } = props ?? {};

        return deletePerson(publicId, personId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/persons/{personId}` },
        { operationId: 'delete_person', operationName: 'deletePerson' }
    );

    return customOptions;
};

export type DeletePersonMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeletePersonHook>>>>;

export type DeletePersonMutationError = ErrorType<ErrorResponse>;
export type DeletePersonMutationVariables = { publicId: string; personId: string };

/**
 * @summary Delete Person
 */
export const useDeletePerson = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeletePersonHook>>>,
            TError,
            DeletePersonMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeletePersonHook>>>,
    TError,
    DeletePersonMutationVariables,
    TContext
> => {
    return useMutation(useDeletePersonMutationOptions(options), queryClient);
};
export const getCreateTopicUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/topics`;
};

/**
 * @summary Create Topic
 */
export const useCreateTopicHook = (): ((
    publicId: string,
    topicInput: TopicInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, topicInput: TopicInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateTopicUrl(publicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(topicInput),
            schema: Recap,
        });
    };
};

export const getCreateTopicMutationKey = () => ['createTopic'] as const;

export const useCreateTopicMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateTopicHook>>>,
        TError,
        CreateTopicMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTopicHook>>>,
    TError,
    CreateTopicMutationVariables,
    TContext
> => {
    const mutationKey = getCreateTopicMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createTopic = useCreateTopicHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateTopicHook>>>,
        CreateTopicMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return createTopic(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/topics` },
        { operationId: 'create_topic', operationName: 'createTopic' }
    );

    return customOptions;
};

export type CreateTopicMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateTopicHook>>>>;
export type CreateTopicMutationBody = TopicInput;
export type CreateTopicMutationError = ErrorType<ErrorResponse>;
export type CreateTopicMutationVariables = { publicId: string; data: TopicInput };

/**
 * @summary Create Topic
 */
export const useCreateTopic = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateTopicHook>>>,
            TError,
            CreateTopicMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateTopicHook>>>,
    TError,
    CreateTopicMutationVariables,
    TContext
> => {
    return useMutation(useCreateTopicMutationOptions(options), queryClient);
};
export const getUpdateTopicUrl = (publicId: string, topicId: string) => {
    return `/workspace/recaps/${publicId}/topics/${topicId}`;
};

/**
 * @summary Update Topic
 */
export const useUpdateTopicHook = (): ((
    publicId: string,
    topicId: string,
    topicInput: TopicInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        topicId: string,
        topicInput: TopicInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateTopicUrl(publicId, topicId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(topicInput),
            schema: Recap,
        });
    };
};

export const getUpdateTopicMutationKey = () => ['updateTopic'] as const;

export const useUpdateTopicMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateTopicHook>>>,
        TError,
        UpdateTopicMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTopicHook>>>,
    TError,
    UpdateTopicMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateTopicMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateTopic = useUpdateTopicHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateTopicHook>>>,
        UpdateTopicMutationVariables
    > = (props) => {
        const { publicId, topicId, data } = props ?? {};

        return updateTopic(publicId, topicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/topics/{topicId}` },
        { operationId: 'update_topic', operationName: 'updateTopic' }
    );

    return customOptions;
};

export type UpdateTopicMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateTopicHook>>>>;
export type UpdateTopicMutationBody = TopicInput;
export type UpdateTopicMutationError = ErrorType<ErrorResponse>;
export type UpdateTopicMutationVariables = { publicId: string; topicId: string; data: TopicInput };

/**
 * @summary Update Topic
 */
export const useUpdateTopic = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateTopicHook>>>,
            TError,
            UpdateTopicMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateTopicHook>>>,
    TError,
    UpdateTopicMutationVariables,
    TContext
> => {
    return useMutation(useUpdateTopicMutationOptions(options), queryClient);
};
export const getDeleteTopicUrl = (publicId: string, topicId: string) => {
    return `/workspace/recaps/${publicId}/topics/${topicId}`;
};

/**
 * @summary Delete Topic
 */
export const useDeleteTopicHook = (): ((
    publicId: string,
    topicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, topicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteTopicUrl(publicId, topicId), {
            ...options,
            method: 'DELETE',

            schema: Recap,
        });
    };
};

export const getDeleteTopicMutationKey = () => ['deleteTopic'] as const;

export const useDeleteTopicMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteTopicHook>>>,
        TError,
        DeleteTopicMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTopicHook>>>,
    TError,
    DeleteTopicMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteTopicMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteTopic = useDeleteTopicHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteTopicHook>>>,
        DeleteTopicMutationVariables
    > = (props) => {
        const { publicId, topicId } = props ?? {};

        return deleteTopic(publicId, topicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/topics/{topicId}` },
        { operationId: 'delete_topic', operationName: 'deleteTopic' }
    );

    return customOptions;
};

export type DeleteTopicMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteTopicHook>>>>;

export type DeleteTopicMutationError = ErrorType<ErrorResponse>;
export type DeleteTopicMutationVariables = { publicId: string; topicId: string };

/**
 * @summary Delete Topic
 */
export const useDeleteTopic = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteTopicHook>>>,
            TError,
            DeleteTopicMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteTopicHook>>>,
    TError,
    DeleteTopicMutationVariables,
    TContext
> => {
    return useMutation(useDeleteTopicMutationOptions(options), queryClient);
};
export const getMoveTopicUrl = (publicId: string, topicId: string) => {
    return `/workspace/recaps/${publicId}/topics/${topicId}/move`;
};

/**
 * @summary Move Topic
 */
export const useMoveTopicHook = (): ((
    publicId: string,
    topicId: string,
    move: Move,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, topicId: string, move: Move, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getMoveTopicUrl(publicId, topicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(move),
            schema: Recap,
        });
    };
};

export const getMoveTopicMutationKey = () => ['moveTopic'] as const;

export const useMoveTopicMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMoveTopicHook>>>,
        TError,
        MoveTopicMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMoveTopicHook>>>,
    TError,
    MoveTopicMutationVariables,
    TContext
> => {
    const mutationKey = getMoveTopicMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const moveTopic = useMoveTopicHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMoveTopicHook>>>,
        MoveTopicMutationVariables
    > = (props) => {
        const { publicId, topicId, data } = props ?? {};

        return moveTopic(publicId, topicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/topics/{topicId}/move` },
        { operationId: 'move_topic', operationName: 'moveTopic' }
    );

    return customOptions;
};

export type MoveTopicMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMoveTopicHook>>>>;
export type MoveTopicMutationBody = Move;
export type MoveTopicMutationError = ErrorType<ErrorResponse>;
export type MoveTopicMutationVariables = { publicId: string; topicId: string; data: Move };

/**
 * @summary Move Topic
 */
export const useMoveTopic = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMoveTopicHook>>>,
            TError,
            MoveTopicMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMoveTopicHook>>>,
    TError,
    MoveTopicMutationVariables,
    TContext
> => {
    return useMutation(useMoveTopicMutationOptions(options), queryClient);
};
export const getCreateDecisionUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/decisions`;
};

/**
 * @summary Create Decision
 */
export const useCreateDecisionHook = (): ((
    publicId: string,
    decisionInput: DecisionInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        decisionInput: DecisionInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getCreateDecisionUrl(publicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(decisionInput),
            schema: Recap,
        });
    };
};

export const getCreateDecisionMutationKey = () => ['createDecision'] as const;

export const useCreateDecisionMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateDecisionHook>>>,
        TError,
        CreateDecisionMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateDecisionHook>>>,
    TError,
    CreateDecisionMutationVariables,
    TContext
> => {
    const mutationKey = getCreateDecisionMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createDecision = useCreateDecisionHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateDecisionHook>>>,
        CreateDecisionMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return createDecision(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/decisions` },
        { operationId: 'create_decision', operationName: 'createDecision' }
    );

    return customOptions;
};

export type CreateDecisionMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateDecisionHook>>>>;
export type CreateDecisionMutationBody = DecisionInput;
export type CreateDecisionMutationError = ErrorType<ErrorResponse>;
export type CreateDecisionMutationVariables = { publicId: string; data: DecisionInput };

/**
 * @summary Create Decision
 */
export const useCreateDecision = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateDecisionHook>>>,
            TError,
            CreateDecisionMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateDecisionHook>>>,
    TError,
    CreateDecisionMutationVariables,
    TContext
> => {
    return useMutation(useCreateDecisionMutationOptions(options), queryClient);
};
export const getUpdateDecisionUrl = (publicId: string, decisionId: string) => {
    return `/workspace/recaps/${publicId}/decisions/${decisionId}`;
};

/**
 * @summary Update Decision
 */
export const useUpdateDecisionHook = (): ((
    publicId: string,
    decisionId: string,
    decisionInput: DecisionInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        decisionId: string,
        decisionInput: DecisionInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateDecisionUrl(publicId, decisionId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(decisionInput),
            schema: Recap,
        });
    };
};

export const getUpdateDecisionMutationKey = () => ['updateDecision'] as const;

export const useUpdateDecisionMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateDecisionHook>>>,
        TError,
        UpdateDecisionMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateDecisionHook>>>,
    TError,
    UpdateDecisionMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateDecisionMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateDecision = useUpdateDecisionHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateDecisionHook>>>,
        UpdateDecisionMutationVariables
    > = (props) => {
        const { publicId, decisionId, data } = props ?? {};

        return updateDecision(publicId, decisionId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/decisions/{decisionId}` },
        { operationId: 'update_decision', operationName: 'updateDecision' }
    );

    return customOptions;
};

export type UpdateDecisionMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateDecisionHook>>>>;
export type UpdateDecisionMutationBody = DecisionInput;
export type UpdateDecisionMutationError = ErrorType<ErrorResponse>;
export type UpdateDecisionMutationVariables = { publicId: string; decisionId: string; data: DecisionInput };

/**
 * @summary Update Decision
 */
export const useUpdateDecision = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateDecisionHook>>>,
            TError,
            UpdateDecisionMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateDecisionHook>>>,
    TError,
    UpdateDecisionMutationVariables,
    TContext
> => {
    return useMutation(useUpdateDecisionMutationOptions(options), queryClient);
};
export const getDeleteDecisionUrl = (publicId: string, decisionId: string) => {
    return `/workspace/recaps/${publicId}/decisions/${decisionId}`;
};

/**
 * @summary Delete Decision
 */
export const useDeleteDecisionHook = (): ((
    publicId: string,
    decisionId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, decisionId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteDecisionUrl(publicId, decisionId), {
            ...options,
            method: 'DELETE',

            schema: Recap,
        });
    };
};

export const getDeleteDecisionMutationKey = () => ['deleteDecision'] as const;

export const useDeleteDecisionMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteDecisionHook>>>,
        TError,
        DeleteDecisionMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteDecisionHook>>>,
    TError,
    DeleteDecisionMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteDecisionMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteDecision = useDeleteDecisionHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteDecisionHook>>>,
        DeleteDecisionMutationVariables
    > = (props) => {
        const { publicId, decisionId } = props ?? {};

        return deleteDecision(publicId, decisionId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/decisions/{decisionId}` },
        { operationId: 'delete_decision', operationName: 'deleteDecision' }
    );

    return customOptions;
};

export type DeleteDecisionMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteDecisionHook>>>>;

export type DeleteDecisionMutationError = ErrorType<ErrorResponse>;
export type DeleteDecisionMutationVariables = { publicId: string; decisionId: string };

/**
 * @summary Delete Decision
 */
export const useDeleteDecision = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteDecisionHook>>>,
            TError,
            DeleteDecisionMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteDecisionHook>>>,
    TError,
    DeleteDecisionMutationVariables,
    TContext
> => {
    return useMutation(useDeleteDecisionMutationOptions(options), queryClient);
};
export const getMoveDecisionUrl = (publicId: string, decisionId: string) => {
    return `/workspace/recaps/${publicId}/decisions/${decisionId}/move`;
};

/**
 * @summary Move Decision
 */
export const useMoveDecisionHook = (): ((
    publicId: string,
    decisionId: string,
    itemMove: ItemMove,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        decisionId: string,
        itemMove: ItemMove,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getMoveDecisionUrl(publicId, decisionId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(itemMove),
            schema: Recap,
        });
    };
};

export const getMoveDecisionMutationKey = () => ['moveDecision'] as const;

export const useMoveDecisionMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMoveDecisionHook>>>,
        TError,
        MoveDecisionMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMoveDecisionHook>>>,
    TError,
    MoveDecisionMutationVariables,
    TContext
> => {
    const mutationKey = getMoveDecisionMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const moveDecision = useMoveDecisionHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMoveDecisionHook>>>,
        MoveDecisionMutationVariables
    > = (props) => {
        const { publicId, decisionId, data } = props ?? {};

        return moveDecision(publicId, decisionId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/decisions/{decisionId}/move` },
        { operationId: 'move_decision', operationName: 'moveDecision' }
    );

    return customOptions;
};

export type MoveDecisionMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMoveDecisionHook>>>>;
export type MoveDecisionMutationBody = ItemMove;
export type MoveDecisionMutationError = ErrorType<ErrorResponse>;
export type MoveDecisionMutationVariables = { publicId: string; decisionId: string; data: ItemMove };

/**
 * @summary Move Decision
 */
export const useMoveDecision = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMoveDecisionHook>>>,
            TError,
            MoveDecisionMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMoveDecisionHook>>>,
    TError,
    MoveDecisionMutationVariables,
    TContext
> => {
    return useMutation(useMoveDecisionMutationOptions(options), queryClient);
};
export const getCreateTodoUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/todos`;
};

/**
 * @summary Create Todo
 */
export const useCreateTodoHook = (): ((
    publicId: string,
    todoInput: TodoInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, todoInput: TodoInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateTodoUrl(publicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(todoInput),
            schema: Recap,
        });
    };
};

export const getCreateTodoMutationKey = () => ['createTodo'] as const;

export const useCreateTodoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateTodoHook>>>,
        TError,
        CreateTodoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTodoHook>>>,
    TError,
    CreateTodoMutationVariables,
    TContext
> => {
    const mutationKey = getCreateTodoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createTodo = useCreateTodoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateTodoHook>>>,
        CreateTodoMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return createTodo(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/todos` },
        { operationId: 'create_todo', operationName: 'createTodo' }
    );

    return customOptions;
};

export type CreateTodoMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateTodoHook>>>>;
export type CreateTodoMutationBody = TodoInput;
export type CreateTodoMutationError = ErrorType<ErrorResponse>;
export type CreateTodoMutationVariables = { publicId: string; data: TodoInput };

/**
 * @summary Create Todo
 */
export const useCreateTodo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateTodoHook>>>,
            TError,
            CreateTodoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateTodoHook>>>,
    TError,
    CreateTodoMutationVariables,
    TContext
> => {
    return useMutation(useCreateTodoMutationOptions(options), queryClient);
};
export const getUpdateTodoUrl = (publicId: string, todoId: string) => {
    return `/workspace/recaps/${publicId}/todos/${todoId}`;
};

/**
 * @summary Update Todo
 */
export const useUpdateTodoHook = (): ((
    publicId: string,
    todoId: string,
    todoInput: TodoInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        todoId: string,
        todoInput: TodoInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateTodoUrl(publicId, todoId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(todoInput),
            schema: Recap,
        });
    };
};

export const getUpdateTodoMutationKey = () => ['updateTodo'] as const;

export const useUpdateTodoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateTodoHook>>>,
        TError,
        UpdateTodoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTodoHook>>>,
    TError,
    UpdateTodoMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateTodoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateTodo = useUpdateTodoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateTodoHook>>>,
        UpdateTodoMutationVariables
    > = (props) => {
        const { publicId, todoId, data } = props ?? {};

        return updateTodo(publicId, todoId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/todos/{todoId}` },
        { operationId: 'update_todo', operationName: 'updateTodo' }
    );

    return customOptions;
};

export type UpdateTodoMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateTodoHook>>>>;
export type UpdateTodoMutationBody = TodoInput;
export type UpdateTodoMutationError = ErrorType<ErrorResponse>;
export type UpdateTodoMutationVariables = { publicId: string; todoId: string; data: TodoInput };

/**
 * @summary Update Todo
 */
export const useUpdateTodo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateTodoHook>>>,
            TError,
            UpdateTodoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateTodoHook>>>,
    TError,
    UpdateTodoMutationVariables,
    TContext
> => {
    return useMutation(useUpdateTodoMutationOptions(options), queryClient);
};
export const getDeleteTodoUrl = (publicId: string, todoId: string) => {
    return `/workspace/recaps/${publicId}/todos/${todoId}`;
};

/**
 * @summary Delete Todo
 */
export const useDeleteTodoHook = (): ((
    publicId: string,
    todoId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, todoId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteTodoUrl(publicId, todoId), {
            ...options,
            method: 'DELETE',

            schema: Recap,
        });
    };
};

export const getDeleteTodoMutationKey = () => ['deleteTodo'] as const;

export const useDeleteTodoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteTodoHook>>>,
        TError,
        DeleteTodoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTodoHook>>>,
    TError,
    DeleteTodoMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteTodoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteTodo = useDeleteTodoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteTodoHook>>>,
        DeleteTodoMutationVariables
    > = (props) => {
        const { publicId, todoId } = props ?? {};

        return deleteTodo(publicId, todoId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/todos/{todoId}` },
        { operationId: 'delete_todo', operationName: 'deleteTodo' }
    );

    return customOptions;
};

export type DeleteTodoMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteTodoHook>>>>;

export type DeleteTodoMutationError = ErrorType<ErrorResponse>;
export type DeleteTodoMutationVariables = { publicId: string; todoId: string };

/**
 * @summary Delete Todo
 */
export const useDeleteTodo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteTodoHook>>>,
            TError,
            DeleteTodoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteTodoHook>>>,
    TError,
    DeleteTodoMutationVariables,
    TContext
> => {
    return useMutation(useDeleteTodoMutationOptions(options), queryClient);
};
export const getMoveTodoUrl = (publicId: string, todoId: string) => {
    return `/workspace/recaps/${publicId}/todos/${todoId}/move`;
};

/**
 * @summary Move Todo
 */
export const useMoveTodoHook = (): ((
    publicId: string,
    todoId: string,
    itemMove: ItemMove,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        todoId: string,
        itemMove: ItemMove,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getMoveTodoUrl(publicId, todoId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(itemMove),
            schema: Recap,
        });
    };
};

export const getMoveTodoMutationKey = () => ['moveTodo'] as const;

export const useMoveTodoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMoveTodoHook>>>,
        TError,
        MoveTodoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMoveTodoHook>>>,
    TError,
    MoveTodoMutationVariables,
    TContext
> => {
    const mutationKey = getMoveTodoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const moveTodo = useMoveTodoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMoveTodoHook>>>,
        MoveTodoMutationVariables
    > = (props) => {
        const { publicId, todoId, data } = props ?? {};

        return moveTodo(publicId, todoId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/todos/{todoId}/move` },
        { operationId: 'move_todo', operationName: 'moveTodo' }
    );

    return customOptions;
};

export type MoveTodoMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMoveTodoHook>>>>;
export type MoveTodoMutationBody = ItemMove;
export type MoveTodoMutationError = ErrorType<ErrorResponse>;
export type MoveTodoMutationVariables = { publicId: string; todoId: string; data: ItemMove };

/**
 * @summary Move Todo
 */
export const useMoveTodo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMoveTodoHook>>>,
            TError,
            MoveTodoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMoveTodoHook>>>,
    TError,
    MoveTodoMutationVariables,
    TContext
> => {
    return useMutation(useMoveTodoMutationOptions(options), queryClient);
};
export const getMarkTodoDoneUrl = (publicId: string, todoId: string) => {
    return `/workspace/recaps/${publicId}/todos/${todoId}/done`;
};

/**
 * @summary Mark Todo Done
 */
export const useMarkTodoDoneHook = (): ((
    publicId: string,
    todoId: string,
    todoDone: TodoDone,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        todoId: string,
        todoDone: TodoDone,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getMarkTodoDoneUrl(publicId, todoId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(todoDone),
            schema: Recap,
        });
    };
};

export const getMarkTodoDoneMutationKey = () => ['markTodoDone'] as const;

export const useMarkTodoDoneMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMarkTodoDoneHook>>>,
        TError,
        MarkTodoDoneMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMarkTodoDoneHook>>>,
    TError,
    MarkTodoDoneMutationVariables,
    TContext
> => {
    const mutationKey = getMarkTodoDoneMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const markTodoDone = useMarkTodoDoneHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMarkTodoDoneHook>>>,
        MarkTodoDoneMutationVariables
    > = (props) => {
        const { publicId, todoId, data } = props ?? {};

        return markTodoDone(publicId, todoId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/todos/{todoId}/done` },
        { operationId: 'mark_todo_done', operationName: 'markTodoDone' }
    );

    return customOptions;
};

export type MarkTodoDoneMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMarkTodoDoneHook>>>>;
export type MarkTodoDoneMutationBody = TodoDone;
export type MarkTodoDoneMutationError = ErrorType<ErrorResponse>;
export type MarkTodoDoneMutationVariables = { publicId: string; todoId: string; data: TodoDone };

/**
 * @summary Mark Todo Done
 */
export const useMarkTodoDone = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMarkTodoDoneHook>>>,
            TError,
            MarkTodoDoneMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMarkTodoDoneHook>>>,
    TError,
    MarkTodoDoneMutationVariables,
    TContext
> => {
    return useMutation(useMarkTodoDoneMutationOptions(options), queryClient);
};
export const getReportTodoExternalLinkUrl = (publicId: string, todoId: string) => {
    return `/workspace/recaps/${publicId}/todos/${todoId}/external-link`;
};

/**
 * @summary Report Todo External Link
 */
export const useReportTodoExternalLinkHook = (): ((
    publicId: string,
    todoId: string,
    todoExternalLinkInput: TodoExternalLinkInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<ExternalLink>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        todoId: string,
        todoExternalLinkInput: TodoExternalLinkInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getReportTodoExternalLinkUrl(publicId, todoId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(todoExternalLinkInput),
            schema: ExternalLink,
        });
    };
};

export const getReportTodoExternalLinkMutationKey = () => ['reportTodoExternalLink'] as const;

export const useReportTodoExternalLinkMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useReportTodoExternalLinkHook>>>,
        TError,
        ReportTodoExternalLinkMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useReportTodoExternalLinkHook>>>,
    TError,
    ReportTodoExternalLinkMutationVariables,
    TContext
> => {
    const mutationKey = getReportTodoExternalLinkMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const reportTodoExternalLink = useReportTodoExternalLinkHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useReportTodoExternalLinkHook>>>,
        ReportTodoExternalLinkMutationVariables
    > = (props) => {
        const { publicId, todoId, data } = props ?? {};

        return reportTodoExternalLink(publicId, todoId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/todos/{todoId}/external-link` },
        { operationId: 'report_todo_external_link', operationName: 'reportTodoExternalLink' }
    );

    return customOptions;
};

export type ReportTodoExternalLinkMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useReportTodoExternalLinkHook>>>
>;
export type ReportTodoExternalLinkMutationBody = TodoExternalLinkInput;
export type ReportTodoExternalLinkMutationError = ErrorType<ErrorResponse>;
export type ReportTodoExternalLinkMutationVariables = { publicId: string; todoId: string; data: TodoExternalLinkInput };

/**
 * @summary Report Todo External Link
 */
export const useReportTodoExternalLink = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useReportTodoExternalLinkHook>>>,
            TError,
            ReportTodoExternalLinkMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useReportTodoExternalLinkHook>>>,
    TError,
    ReportTodoExternalLinkMutationVariables,
    TContext
> => {
    return useMutation(useReportTodoExternalLinkMutationOptions(options), queryClient);
};
export const getCreateEventUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/events`;
};

/**
 * @summary Create Event
 */
export const useCreateEventHook = (): ((
    publicId: string,
    eventInput: EventInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, eventInput: EventInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateEventUrl(publicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(eventInput),
            schema: Recap,
        });
    };
};

export const getCreateEventMutationKey = () => ['createEvent'] as const;

export const useCreateEventMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateEventHook>>>,
        TError,
        CreateEventMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateEventHook>>>,
    TError,
    CreateEventMutationVariables,
    TContext
> => {
    const mutationKey = getCreateEventMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createEvent = useCreateEventHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateEventHook>>>,
        CreateEventMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return createEvent(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/events` },
        { operationId: 'create_event', operationName: 'createEvent' }
    );

    return customOptions;
};

export type CreateEventMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateEventHook>>>>;
export type CreateEventMutationBody = EventInput;
export type CreateEventMutationError = ErrorType<ErrorResponse>;
export type CreateEventMutationVariables = { publicId: string; data: EventInput };

/**
 * @summary Create Event
 */
export const useCreateEvent = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateEventHook>>>,
            TError,
            CreateEventMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateEventHook>>>,
    TError,
    CreateEventMutationVariables,
    TContext
> => {
    return useMutation(useCreateEventMutationOptions(options), queryClient);
};
export const getUpdateEventUrl = (publicId: string, eventId: string) => {
    return `/workspace/recaps/${publicId}/events/${eventId}`;
};

/**
 * @summary Update Event
 */
export const useUpdateEventHook = (): ((
    publicId: string,
    eventId: string,
    eventInput: EventInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        eventId: string,
        eventInput: EventInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateEventUrl(publicId, eventId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(eventInput),
            schema: Recap,
        });
    };
};

export const getUpdateEventMutationKey = () => ['updateEvent'] as const;

export const useUpdateEventMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateEventHook>>>,
        TError,
        UpdateEventMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateEventHook>>>,
    TError,
    UpdateEventMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateEventMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateEvent = useUpdateEventHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateEventHook>>>,
        UpdateEventMutationVariables
    > = (props) => {
        const { publicId, eventId, data } = props ?? {};

        return updateEvent(publicId, eventId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/events/{eventId}` },
        { operationId: 'update_event', operationName: 'updateEvent' }
    );

    return customOptions;
};

export type UpdateEventMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateEventHook>>>>;
export type UpdateEventMutationBody = EventInput;
export type UpdateEventMutationError = ErrorType<ErrorResponse>;
export type UpdateEventMutationVariables = { publicId: string; eventId: string; data: EventInput };

/**
 * @summary Update Event
 */
export const useUpdateEvent = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateEventHook>>>,
            TError,
            UpdateEventMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateEventHook>>>,
    TError,
    UpdateEventMutationVariables,
    TContext
> => {
    return useMutation(useUpdateEventMutationOptions(options), queryClient);
};
export const getDeleteEventUrl = (publicId: string, eventId: string) => {
    return `/workspace/recaps/${publicId}/events/${eventId}`;
};

/**
 * @summary Delete Event
 */
export const useDeleteEventHook = (): ((
    publicId: string,
    eventId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, eventId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteEventUrl(publicId, eventId), {
            ...options,
            method: 'DELETE',

            schema: Recap,
        });
    };
};

export const getDeleteEventMutationKey = () => ['deleteEvent'] as const;

export const useDeleteEventMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteEventHook>>>,
        TError,
        DeleteEventMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteEventHook>>>,
    TError,
    DeleteEventMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteEventMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteEvent = useDeleteEventHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteEventHook>>>,
        DeleteEventMutationVariables
    > = (props) => {
        const { publicId, eventId } = props ?? {};

        return deleteEvent(publicId, eventId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/events/{eventId}` },
        { operationId: 'delete_event', operationName: 'deleteEvent' }
    );

    return customOptions;
};

export type DeleteEventMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteEventHook>>>>;

export type DeleteEventMutationError = ErrorType<ErrorResponse>;
export type DeleteEventMutationVariables = { publicId: string; eventId: string };

/**
 * @summary Delete Event
 */
export const useDeleteEvent = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteEventHook>>>,
            TError,
            DeleteEventMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteEventHook>>>,
    TError,
    DeleteEventMutationVariables,
    TContext
> => {
    return useMutation(useDeleteEventMutationOptions(options), queryClient);
};
export const getMoveEventUrl = (publicId: string, eventId: string) => {
    return `/workspace/recaps/${publicId}/events/${eventId}/move`;
};

/**
 * @summary Move Event
 */
export const useMoveEventHook = (): ((
    publicId: string,
    eventId: string,
    itemMove: ItemMove,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        eventId: string,
        itemMove: ItemMove,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getMoveEventUrl(publicId, eventId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(itemMove),
            schema: Recap,
        });
    };
};

export const getMoveEventMutationKey = () => ['moveEvent'] as const;

export const useMoveEventMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMoveEventHook>>>,
        TError,
        MoveEventMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMoveEventHook>>>,
    TError,
    MoveEventMutationVariables,
    TContext
> => {
    const mutationKey = getMoveEventMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const moveEvent = useMoveEventHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMoveEventHook>>>,
        MoveEventMutationVariables
    > = (props) => {
        const { publicId, eventId, data } = props ?? {};

        return moveEvent(publicId, eventId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/events/{eventId}/move` },
        { operationId: 'move_event', operationName: 'moveEvent' }
    );

    return customOptions;
};

export type MoveEventMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMoveEventHook>>>>;
export type MoveEventMutationBody = ItemMove;
export type MoveEventMutationError = ErrorType<ErrorResponse>;
export type MoveEventMutationVariables = { publicId: string; eventId: string; data: ItemMove };

/**
 * @summary Move Event
 */
export const useMoveEvent = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMoveEventHook>>>,
            TError,
            MoveEventMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMoveEventHook>>>,
    TError,
    MoveEventMutationVariables,
    TContext
> => {
    return useMutation(useMoveEventMutationOptions(options), queryClient);
};
export const getGetDraftChangesUrl = (publicId: string | null) => {
    return `/workspace/recaps/${publicId}/draft/changes`;
};

/**
 * @summary Get Draft Changes
 */
export const useGetDraftChangesHook = (): ((
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<RecapChanges>) => {
    const customFetcher = useApiFetch();
    return (publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetDraftChangesUrl(publicId), {
            ...options,
            method: 'GET',

            schema: RecapChanges,
        });
    };
};

export const getGetDraftChangesQueryKey = (publicId: string | null) => {
    return ['workspace', 'recaps', publicId, 'draft', 'changes'] as const;
};

export const useGetDraftChangesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetDraftChangesQueryKey(publicId);

    const getDraftChanges = useGetDraftChangesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>> = ({ signal }) =>
        getDraftChanges(publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions },
        { publicId },
        {
            url: `/workspace/recaps/${publicId}/draft/changes`,
            operationId: 'get_draft_changes',
            operationName: 'getDraftChanges',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDraftChangesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>>;
export type GetDraftChangesQueryError = ErrorType<ErrorResponse>;

export function useGetDraftChanges<
    TData = Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDraftChanges<
    TData = Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetDraftChanges<
    TData = Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Draft Changes
 */

export function useGetDraftChanges<
    TData = Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDraftChangesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetDraftChangesQueryOptions(publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getPublishRecapUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/publish`;
};

/**
 * @summary Publish Recap
 */
export const usePublishRecapHook = (): ((
    publicId: string,
    recapPublish: RecapPublish,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (publicId: string, recapPublish: RecapPublish, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getPublishRecapUrl(publicId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(recapPublish),
            schema: Recap,
        });
    };
};

export const getPublishRecapMutationKey = () => ['publishRecap'] as const;

export const usePublishRecapMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof usePublishRecapHook>>>,
        TError,
        PublishRecapMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePublishRecapHook>>>,
    TError,
    PublishRecapMutationVariables,
    TContext
> => {
    const mutationKey = getPublishRecapMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const publishRecap = usePublishRecapHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof usePublishRecapHook>>>,
        PublishRecapMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return publishRecap(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/publish` },
        { operationId: 'publish_recap', operationName: 'publishRecap' }
    );

    return customOptions;
};

export type PublishRecapMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePublishRecapHook>>>>;
export type PublishRecapMutationBody = RecapPublish;
export type PublishRecapMutationError = ErrorType<ErrorResponse>;
export type PublishRecapMutationVariables = { publicId: string; data: RecapPublish };

/**
 * @summary Publish Recap
 */
export const usePublishRecap = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof usePublishRecapHook>>>,
            TError,
            PublishRecapMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof usePublishRecapHook>>>,
    TError,
    PublishRecapMutationVariables,
    TContext
> => {
    return useMutation(usePublishRecapMutationOptions(options), queryClient);
};
export const getListSupersedableUrl = (publicId: string | null) => {
    return `/workspace/recaps/${publicId}/supersedable`;
};

/**
 * @summary List Supersedable
 */
export const useListSupersedableHook = (): ((
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SupersedableItem[]>) => {
    const customFetcher = useApiFetch();
    return (publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListSupersedableUrl(publicId), {
            ...options,
            method: 'GET',

            schema: zod.array(SupersedableItem),
        });
    };
};

export const getListSupersedableQueryKey = (publicId: string | null) => {
    return ['workspace', 'recaps', publicId, 'supersedable'] as const;
};

export const useListSupersedableQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListSupersedableQueryKey(publicId);

    const listSupersedable = useListSupersedableHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>> = ({ signal }) =>
        listSupersedable(publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions },
        { publicId },
        {
            url: `/workspace/recaps/${publicId}/supersedable`,
            operationId: 'list_supersedable',
            operationName: 'listSupersedable',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSupersedableQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>>;
export type ListSupersedableQueryError = ErrorType<ErrorResponse>;

export function useListSupersedable<
    TData = Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSupersedable<
    TData = Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSupersedable<
    TData = Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Supersedable
 */

export function useListSupersedable<
    TData = Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSupersedableHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListSupersedableQueryOptions(publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateAcknowledgersUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/acknowledgers`;
};

/**
 * @summary Update Acknowledgers
 */
export const useUpdateAcknowledgersHook = (): ((
    publicId: string,
    recapAcknowledgersUpdate: RecapAcknowledgersUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        recapAcknowledgersUpdate: RecapAcknowledgersUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateAcknowledgersUrl(publicId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(recapAcknowledgersUpdate),
            schema: Recap,
        });
    };
};

export const getUpdateAcknowledgersMutationKey = () => ['updateAcknowledgers'] as const;

export const useUpdateAcknowledgersMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateAcknowledgersHook>>>,
        TError,
        UpdateAcknowledgersMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateAcknowledgersHook>>>,
    TError,
    UpdateAcknowledgersMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateAcknowledgersMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateAcknowledgers = useUpdateAcknowledgersHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateAcknowledgersHook>>>,
        UpdateAcknowledgersMutationVariables
    > = (props) => {
        const { publicId, data } = props ?? {};

        return updateAcknowledgers(publicId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/acknowledgers` },
        { operationId: 'update_acknowledgers', operationName: 'updateAcknowledgers' }
    );

    return customOptions;
};

export type UpdateAcknowledgersMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateAcknowledgersHook>>>
>;
export type UpdateAcknowledgersMutationBody = RecapAcknowledgersUpdate;
export type UpdateAcknowledgersMutationError = ErrorType<ErrorResponse>;
export type UpdateAcknowledgersMutationVariables = { publicId: string; data: RecapAcknowledgersUpdate };

/**
 * @summary Update Acknowledgers
 */
export const useUpdateAcknowledgers = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateAcknowledgersHook>>>,
            TError,
            UpdateAcknowledgersMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateAcknowledgersHook>>>,
    TError,
    UpdateAcknowledgersMutationVariables,
    TContext
> => {
    return useMutation(useUpdateAcknowledgersMutationOptions(options), queryClient);
};
export const getDiscardDraftUrl = (publicId: string) => {
    return `/workspace/recaps/${publicId}/draft/discard`;
};

/**
 * @summary Discard Draft
 */
export const useDiscardDraftHook = (): ((
    publicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDiscardDraftUrl(publicId), {
            ...options,
            method: 'POST',

            schema: Recap,
        });
    };
};

export const getDiscardDraftMutationKey = () => ['discardDraft'] as const;

export const useDiscardDraftMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDiscardDraftHook>>>,
        TError,
        DiscardDraftMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDiscardDraftHook>>>,
    TError,
    DiscardDraftMutationVariables,
    TContext
> => {
    const mutationKey = getDiscardDraftMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const discardDraft = useDiscardDraftHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDiscardDraftHook>>>,
        DiscardDraftMutationVariables
    > = (props) => {
        const { publicId } = props ?? {};

        return discardDraft(publicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/draft/discard` },
        { operationId: 'discard_draft', operationName: 'discardDraft' }
    );

    return customOptions;
};

export type DiscardDraftMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDiscardDraftHook>>>>;

export type DiscardDraftMutationError = ErrorType<ErrorResponse>;
export type DiscardDraftMutationVariables = { publicId: string };

/**
 * @summary Discard Draft
 */
export const useDiscardDraft = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDiscardDraftHook>>>,
            TError,
            DiscardDraftMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDiscardDraftHook>>>,
    TError,
    DiscardDraftMutationVariables,
    TContext
> => {
    return useMutation(useDiscardDraftMutationOptions(options), queryClient);
};
export const getListVersionsUrl = (publicId: string | null) => {
    return `/workspace/recaps/${publicId}/versions`;
};

/**
 * @summary List Versions
 */
export const useListVersionsHook = (): ((
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<RecapVersionListItem[]>) => {
    const customFetcher = useApiFetch();
    return (publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListVersionsUrl(publicId), {
            ...options,
            method: 'GET',

            schema: zod.array(RecapVersionListItem),
        });
    };
};

export const getListVersionsQueryKey = (publicId: string | null) => {
    return ['workspace', 'recaps', publicId, 'versions'] as const;
};

export const useListVersionsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListVersionsQueryKey(publicId);

    const listVersions = useListVersionsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>> = ({ signal }) =>
        listVersions(publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions },
        { publicId },
        { url: `/workspace/recaps/${publicId}/versions`, operationId: 'list_versions', operationName: 'listVersions' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListVersionsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>>;
export type ListVersionsQueryError = ErrorType<ErrorResponse>;

export function useListVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Versions
 */

export function useListVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListVersionsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListVersionsQueryOptions(publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetVersionUrl = (publicId: string | null, number: number | null) => {
    return `/workspace/recaps/${publicId}/versions/${number}`;
};

/**
 * @summary Get Version
 */
export const useGetVersionHook = (): ((
    publicId: string | null,
    number: number | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<RecapVersion>) => {
    const customFetcher = useApiFetch();
    return (
        publicId: string | null,
        number: number | null,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getGetVersionUrl(publicId, number), {
            ...options,
            method: 'GET',

            schema: RecapVersion,
        });
    };
};

export const getGetVersionQueryKey = (publicId: string | null, number: number | null) => {
    return ['workspace', 'recaps', publicId, 'versions', number] as const;
};

export const useGetVersionQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetVersionQueryKey(publicId, number);

    const getVersion = useGetVersionHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>> = ({ signal }) =>
        getVersion(publicId, number, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        {
            queryKey,
            queryFn,
            enabled: publicId !== null && publicId !== undefined && number !== null && number !== undefined,
            ...queryOptions,
        },
        { publicId, number },
        {
            url: `/workspace/recaps/${publicId}/versions/${number}`,
            operationId: 'get_version',
            operationName: 'getVersion',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetVersionQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>>;
export type GetVersionQueryError = ErrorType<ErrorResponse>;

export function useGetVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Version
 */

export function useGetVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetVersionHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetVersionQueryOptions(publicId, number, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getRestoreVersionUrl = (publicId: string, number: number) => {
    return `/workspace/recaps/${publicId}/versions/${number}/restore`;
};

/**
 * @summary Restore Version
 */
export const useRestoreVersionHook = (): ((
    publicId: string,
    number: number,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Recap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, number: number, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getRestoreVersionUrl(publicId, number), {
            ...options,
            method: 'POST',

            schema: Recap,
        });
    };
};

export const getRestoreVersionMutationKey = () => ['restoreVersion'] as const;

export const useRestoreVersionMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRestoreVersionHook>>>,
        TError,
        RestoreVersionMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRestoreVersionHook>>>,
    TError,
    RestoreVersionMutationVariables,
    TContext
> => {
    const mutationKey = getRestoreVersionMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const restoreVersion = useRestoreVersionHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useRestoreVersionHook>>>,
        RestoreVersionMutationVariables
    > = (props) => {
        const { publicId, number } = props ?? {};

        return restoreVersion(publicId, number, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/recaps/{publicId}/versions/{number}/restore` },
        { operationId: 'restore_version', operationName: 'restoreVersion' }
    );

    return customOptions;
};

export type RestoreVersionMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useRestoreVersionHook>>>>;

export type RestoreVersionMutationError = ErrorType<ErrorResponse>;
export type RestoreVersionMutationVariables = { publicId: string; number: number };

/**
 * @summary Restore Version
 */
export const useRestoreVersion = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRestoreVersionHook>>>,
            TError,
            RestoreVersionMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useRestoreVersionHook>>>,
    TError,
    RestoreVersionMutationVariables,
    TContext
> => {
    return useMutation(useRestoreVersionMutationOptions(options), queryClient);
};
export const getListEmployeeRecapsUrl = (params?: ListEmployeeRecapsParams) => {
    const normalizedParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined) {
            normalizedParams.append(key, value === null ? 'null' : String(value));
        }
    });

    const stringifiedParams = normalizedParams.toString();

    return stringifiedParams.length > 0 ? `/workspace/client/recaps?${stringifiedParams}` : `/workspace/client/recaps`;
};

/**
 * @summary List Employee Recaps
 */
export const useListEmployeeRecapsHook = (): ((
    params?: ListEmployeeRecapsParams,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<PagedEmployeeRecapListItem>) => {
    const customFetcher = useApiFetch();
    return (params?: ListEmployeeRecapsParams, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListEmployeeRecapsUrl(params), {
            ...options,
            method: 'GET',

            schema: PagedEmployeeRecapListItem,
        });
    };
};

export const getListEmployeeRecapsQueryKey = (params?: ListEmployeeRecapsParams) => {
    return ['workspace', 'client', 'recaps', ...(params ? [params] : [])] as const;
};

export const useListEmployeeRecapsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListEmployeeRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListEmployeeRecapsQueryKey(params);

    const listEmployeeRecaps = useListEmployeeRecapsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>> = ({ signal }) =>
        listEmployeeRecaps(params, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        { params },
        { url: `/workspace/client/recaps`, operationId: 'list_employee_recaps', operationName: 'listEmployeeRecaps' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListEmployeeRecapsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>
>;
export type ListEmployeeRecapsQueryError = ErrorType<ErrorResponse>;

export function useListEmployeeRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params: undefined | ListEmployeeRecapsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployeeRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListEmployeeRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployeeRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListEmployeeRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Employee Recaps
 */

export function useListEmployeeRecaps<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    params?: ListEmployeeRecapsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeRecapsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListEmployeeRecapsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetEmployeeRecapUrl = (publicId: string | null) => {
    return `/workspace/client/recaps/${publicId}`;
};

/**
 * @summary Get Employee Recap
 */
export const useGetEmployeeRecapHook = (): ((
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeRecap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetEmployeeRecapUrl(publicId), {
            ...options,
            method: 'GET',

            schema: EmployeeRecap,
        });
    };
};

export const getGetEmployeeRecapQueryKey = (publicId: string | null) => {
    return ['workspace', 'client', 'recaps', publicId] as const;
};

export const useGetEmployeeRecapQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetEmployeeRecapQueryKey(publicId);

    const getEmployeeRecap = useGetEmployeeRecapHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>> = ({ signal }) =>
        getEmployeeRecap(publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions },
        { publicId },
        {
            url: `/workspace/client/recaps/${publicId}`,
            operationId: 'get_employee_recap',
            operationName: 'getEmployeeRecap',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmployeeRecapQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>>;
export type GetEmployeeRecapQueryError = ErrorType<ErrorResponse>;

export function useGetEmployeeRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Employee Recap
 */

export function useGetEmployeeRecap<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeRecapHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetEmployeeRecapQueryOptions(publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getListEmployeeVersionsUrl = (publicId: string | null) => {
    return `/workspace/client/recaps/${publicId}/versions`;
};

/**
 * @summary List Employee Versions
 */
export const useListEmployeeVersionsHook = (): ((
    publicId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeRecapVersionListItem[]>) => {
    const customFetcher = useApiFetch();
    return (publicId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListEmployeeVersionsUrl(publicId), {
            ...options,
            method: 'GET',

            schema: zod.array(EmployeeRecapVersionListItem),
        });
    };
};

export const getListEmployeeVersionsQueryKey = (publicId: string | null) => {
    return ['workspace', 'client', 'recaps', publicId, 'versions'] as const;
};

export const useListEmployeeVersionsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListEmployeeVersionsQueryKey(publicId);

    const listEmployeeVersions = useListEmployeeVersionsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>> = ({ signal }) =>
        listEmployeeVersions(publicId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions },
        { publicId },
        {
            url: `/workspace/client/recaps/${publicId}/versions`,
            operationId: 'list_employee_versions',
            operationName: 'listEmployeeVersions',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListEmployeeVersionsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>
>;
export type ListEmployeeVersionsQueryError = ErrorType<ErrorResponse>;

export function useListEmployeeVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployeeVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployeeVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Employee Versions
 */

export function useListEmployeeVersions<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeVersionsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListEmployeeVersionsQueryOptions(publicId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetEmployeeVersionUrl = (publicId: string | null, number: number | null) => {
    return `/workspace/client/recaps/${publicId}/versions/${number}`;
};

/**
 * @summary Get Employee Version
 */
export const useGetEmployeeVersionHook = (): ((
    publicId: string | null,
    number: number | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<RecapVersionDetail>) => {
    const customFetcher = useApiFetch();
    return (
        publicId: string | null,
        number: number | null,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getGetEmployeeVersionUrl(publicId, number), {
            ...options,
            method: 'GET',

            schema: RecapVersionDetail,
        });
    };
};

export const getGetEmployeeVersionQueryKey = (publicId: string | null, number: number | null) => {
    return ['workspace', 'client', 'recaps', publicId, 'versions', number] as const;
};

export const useGetEmployeeVersionQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetEmployeeVersionQueryKey(publicId, number);

    const getEmployeeVersion = useGetEmployeeVersionHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>> = ({ signal }) =>
        getEmployeeVersion(publicId, number, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        {
            queryKey,
            queryFn,
            enabled: publicId !== null && publicId !== undefined && number !== null && number !== undefined,
            ...queryOptions,
        },
        { publicId, number },
        {
            url: `/workspace/client/recaps/${publicId}/versions/${number}`,
            operationId: 'get_employee_version',
            operationName: 'getEmployeeVersion',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmployeeVersionQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>
>;
export type GetEmployeeVersionQueryError = ErrorType<ErrorResponse>;

export function useGetEmployeeVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Employee Version
 */

export function useGetEmployeeVersion<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    publicId: string | null,
    number: number | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeVersionHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetEmployeeVersionQueryOptions(publicId, number, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getAcknowledgeRecapUrl = (publicId: string) => {
    return `/workspace/client/recaps/${publicId}/acknowledge`;
};

/**
 * @summary Acknowledge Recap
 */
export const useAcknowledgeRecapHook = (): ((
    publicId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeRecap>) => {
    const customFetcher = useApiFetch();
    return (publicId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getAcknowledgeRecapUrl(publicId), {
            ...options,
            method: 'POST',

            schema: EmployeeRecap,
        });
    };
};

export const getAcknowledgeRecapMutationKey = () => ['acknowledgeRecap'] as const;

export const useAcknowledgeRecapMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAcknowledgeRecapHook>>>,
        TError,
        AcknowledgeRecapMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAcknowledgeRecapHook>>>,
    TError,
    AcknowledgeRecapMutationVariables,
    TContext
> => {
    const mutationKey = getAcknowledgeRecapMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const acknowledgeRecap = useAcknowledgeRecapHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAcknowledgeRecapHook>>>,
        AcknowledgeRecapMutationVariables
    > = (props) => {
        const { publicId } = props ?? {};

        return acknowledgeRecap(publicId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/client/recaps/{publicId}/acknowledge` },
        { operationId: 'acknowledge_recap', operationName: 'acknowledgeRecap' }
    );

    return customOptions;
};

export type AcknowledgeRecapMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAcknowledgeRecapHook>>>
>;

export type AcknowledgeRecapMutationError = ErrorType<ErrorResponse>;
export type AcknowledgeRecapMutationVariables = { publicId: string };

/**
 * @summary Acknowledge Recap
 */
export const useAcknowledgeRecap = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAcknowledgeRecapHook>>>,
            TError,
            AcknowledgeRecapMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAcknowledgeRecapHook>>>,
    TError,
    AcknowledgeRecapMutationVariables,
    TContext
> => {
    return useMutation(useAcknowledgeRecapMutationOptions(options), queryClient);
};
export const getMarkEmployeeTodoDoneUrl = (publicId: string, todoId: string) => {
    return `/workspace/client/recaps/${publicId}/todos/${todoId}/done`;
};

/**
 * @summary Mark Employee Todo Done
 */
export const useMarkEmployeeTodoDoneHook = (): ((
    publicId: string,
    todoId: string,
    todoDone: TodoDone,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeRecap>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        publicId: string,
        todoId: string,
        todoDone: TodoDone,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getMarkEmployeeTodoDoneUrl(publicId, todoId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(todoDone),
            schema: EmployeeRecap,
        });
    };
};

export const getMarkEmployeeTodoDoneMutationKey = () => ['markEmployeeTodoDone'] as const;

export const useMarkEmployeeTodoDoneMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMarkEmployeeTodoDoneHook>>>,
        TError,
        MarkEmployeeTodoDoneMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMarkEmployeeTodoDoneHook>>>,
    TError,
    MarkEmployeeTodoDoneMutationVariables,
    TContext
> => {
    const mutationKey = getMarkEmployeeTodoDoneMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const markEmployeeTodoDone = useMarkEmployeeTodoDoneHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMarkEmployeeTodoDoneHook>>>,
        MarkEmployeeTodoDoneMutationVariables
    > = (props) => {
        const { publicId, todoId, data } = props ?? {};

        return markEmployeeTodoDone(publicId, todoId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/client/recaps/{publicId}/todos/{todoId}/done` },
        { operationId: 'mark_employee_todo_done', operationName: 'markEmployeeTodoDone' }
    );

    return customOptions;
};

export type MarkEmployeeTodoDoneMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useMarkEmployeeTodoDoneHook>>>
>;
export type MarkEmployeeTodoDoneMutationBody = TodoDone;
export type MarkEmployeeTodoDoneMutationError = ErrorType<ErrorResponse>;
export type MarkEmployeeTodoDoneMutationVariables = { publicId: string; todoId: string; data: TodoDone };

/**
 * @summary Mark Employee Todo Done
 */
export const useMarkEmployeeTodoDone = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMarkEmployeeTodoDoneHook>>>,
            TError,
            MarkEmployeeTodoDoneMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMarkEmployeeTodoDoneHook>>>,
    TError,
    MarkEmployeeTodoDoneMutationVariables,
    TContext
> => {
    return useMutation(useMarkEmployeeTodoDoneMutationOptions(options), queryClient);
};
